import type { PlatformSaleItemEntity } from '~/types/sale.type';

export type UseSdpParamsCheckerReturnType = void;

/**
 * Validates the route params of a sale detail page against the sale that was loaded, redirecting to
 * the canonical slug when only the slug is stale.
 *
 * A failed fetch is none of its business: useWatchQueryError covers that. The slug comparison is
 * deliberately guarded by `sale.value` so that a sale missing because the request failed is not
 * reported as a wrong slug, which would mask the real status behind a 404. Runs once, since the
 * page is keyed by slug and a different sale means a new component instance.
 */
export const useSdpParamsChecker = (sale: Ref<PlatformSaleItemEntity | undefined>): UseSdpParamsCheckerReturnType => {
    const { id, slug } = useSdpParams();
    const route = useRoute();
    const routeBaseName = useRouteBaseName();

    if (sale.value && isSdpRoute(routeBaseName(route)) && slug.value !== sale.value.slug && slug.value.endsWith(sale.value.id)) {
        // Permanent redirect: the stale slug is a former shape of this same sale, never a URL of its own (BSD-4629).
        // The query is carried over so campaign attribution survives a landing on an old slug.
        navigateTo({ name: route.name, params: { slug: sale.value.slug }, query: route.query }, { redirectCode: 301 });
    } else if (!id.value || (sale.value && slug.value !== sale.value.slug)) {
        showError({ statusCode: 404 });
    }
};
